
import { useEffect } from "react";
import { Table, TableContainer, Thead, Tr, Th, Tbody, Td, Box, Button, SkeletonText } from "@chakra-ui/react"
import { format } from "date-fns";

import CompletedIcon from "@/assets/CompletedIcon";
import { formatCurrency } from "@/utils/currency";
import ApprovedIcon from "@/assets/ApprovedIcon";
import NewIcon from "@/assets/NewIcon";
import RejectIcon from "@/assets/RejectIcon";
import PaidIcon from "@/assets/PaidIcon";
import UnpaidIcon from "@/assets/UnpaidIcon";
import FileIcon from "@/assets/FileIcon";
import { COLORS, INVOICE_STATUS, PAYMENT_STATUS, ROLES, DURATION_TOAST } from "@/common/constants";
import { useAppDispatch } from "@/redux/hooks";
import { setDataMultiplePayment } from "@/redux/slices/paymentSlice";
import Pagination from "../Layouts/Pagination";
import { useToast } from "@chakra-ui/react";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";

const TableInvoiceInspectorSuperAdmin = ({ /*setIsValidToPay,*/ isLoading, dataList, currentPageSize, handleChangePageSize, handleChangePageNumber, currentPageNumber, totalPageNumber, hasNextPage, handleView, handlePay, handleDownload, handleDownloadZip, role }: any) => {
    const dispatch = useAppDispatch();
    const toast = useToast();
    const dataRaw = useAppSelector((state: RootState) => state.payment.multiplePayment);

    const NUM_COLUMNS = 12;

    // Clear selection when global multiplePayment is cleared
    useEffect(() => {
        // if (Array.isArray(dataRaw?.id) && dataRaw.id.length === 0) {
        //     setSelectedIds([]);
        // }
    }, [dataRaw]);

    // const selectableIds = dataList?.filter((item: any) => item.paymentStatus === PAYMENT_STATUS.UNPAID && item.invoiceStatus === INVOICE_STATUS.APPROVED).map((item: any) => item.id) || [];
    // const isAllSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length;


    // const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     let newSelectedIds: string[] = [];
    //     if (e.target.checked) {
    //         newSelectedIds = selectableIds;
    //         setSelectedIds(selectableIds);
    //     } else {
    //         newSelectedIds = [];
    //         setSelectedIds([]);
    //     }
    //     const selectedData = dataList?.filter((item: any) => newSelectedIds.includes(item.id)) || [];
    //     const obj = {
    //         id: newSelectedIds,
    //         data: selectedData
    //     };
    //     dispatch(setDataMultiplePayment(obj));
    // };

    const handleSelectOne = (id: string) => {
        // Get current global selected IDs
        let globalSelectedIds = Array.isArray(dataRaw?.id) ? [...dataRaw.id] : [];
        // Get current global selected data
        let globalSelectedData = Array.isArray(dataRaw?.data) ? [...dataRaw.data] : [];

        // Find item in current page
        const item = dataList?.find((itm: any) => itm.id === id);
        // Validate currency if adding
        if (!globalSelectedIds.includes(id) && item) {
            const checkedItems = globalSelectedData;
            if (checkedItems.length > 0) {
                const isSameCurrency = checkedItems.find((x: any) => x.currency === item.currency);
                const isSameCompany = checkedItems.find((x: any) => x.companyName === item.companyName);
                if (!isSameCurrency) {
                    toast({
                        title: 'Invalid selection of invoices',
                        description: "You can only select invoices that are same currency.",
                        status: 'info',
                        duration: DURATION_TOAST,
                        isClosable: true,
                    });
                    return;
                }

                if (!isSameCompany) {
                    toast({
                        title: 'Invalid selection of invoices',
                        description: "You can only select invoices that are from same inspector company.",
                        status: 'info',
                        duration: DURATION_TOAST,
                        isClosable: true,
                    });
                    return;
                }
            }
        }

        // Toggle selection
        if (globalSelectedIds.includes(id)) {
            // Uncheck: remove id and data
            globalSelectedIds = globalSelectedIds.filter((itemId) => itemId !== id);
            globalSelectedData = globalSelectedData.filter((itm: any) => itm.id !== id);
        } else {
            // Check: add id and data
            globalSelectedIds.push(id);
            if (item) globalSelectedData.push(item);
        }

        // Dispatch global selection with new array reference for data
        const obj = {
            id: [...globalSelectedIds],
            data: [...globalSelectedData]
        };
        dispatch(setDataMultiplePayment(obj));
    };

    return (
        <TableContainer bgColor="#fff" borderRadius="13">
            <Box overflowX="auto">
                <Table variant='simple' key={JSON.stringify(dataRaw?.id) + JSON.stringify(dataRaw?.data)}>
                    <Thead>
                        <Tr>
                            {role === ROLES.FINANCE && (
                                <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>
                                    {/* <input
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        disabled={selectableIds.length === 0}
                                    /> */}
                                </Th>
                            )}
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Order Number</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Invoice ID</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Invoice Date</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Inspector Name</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Market</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Vessel Name</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Amount</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Invoice File</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Inspection Result</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Invoice Status</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Payment Status</Th>
                            {role === ROLES.FINANCE
                                ? <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Payment Confirmation</Th>
                                : <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Invoice Confirmation</Th>}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading &&
                            <Tr>
                                <Td colSpan={NUM_COLUMNS}>
                                    <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='3' />
                                </Td>
                            </Tr>
                        }
                        {(!isLoading && (!dataList || dataList.length === 0)) &&
                            <Tr>
                                <Td colSpan={NUM_COLUMNS} textAlign="center">Data not found</Td>
                            </Tr>
                        }
                        {!isLoading && dataList?.map((item: any) => {
                            const isSelectable = item.paymentStatus !== PAYMENT_STATUS.UNPAID || item.invoiceStatus !== INVOICE_STATUS.APPROVED
                            return (
                                <Tr key={item.id}>
                                    {role === ROLES.FINANCE && (
                                        <Td>
                                            <input
                                                type="checkbox"
                                                checked={Array.isArray(dataRaw?.data) ? dataRaw.data.some((itm: any) => itm.id === item.id) : false}
                                                onChange={() => handleSelectOne(item.id)}
                                                disabled={isSelectable}
                                            />
                                        </Td>
                                    )}
                                    <Td>{item.sequenceID || '-'}</Td>
                                    <Td>{item.invoiceNumber}</Td>
                                    <Td>{item.invoiceDate ? format(item.invoiceDate, 'dd-MM-yyyy') : '-'}</Td>
                                    <Td>{item.companyName || '-'}</Td>
                                    <Td>{item.market || '-'}</Td>
                                    <Td>{item.shipName}</Td>
                                    <Td textAlign='right'>{item.amount ? formatCurrency(item.amount, item.currency, item.currency == 'IDR' ? 'id-ID' : 'en-US') : '-'}</Td>
                                    <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {item.invoiceDownloadId && <FileIcon onClick={() => handleDownloadZip(item.id, 1)} style={{ cursor: 'pointer' }} />}
                                            {!item.invoiceDownloadId && <p>-</p>}
                                        </div>
                                    </Td>
                                    <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {item.inspectionResultId && <FileIcon onClick={() => handleDownload(item.inspectionResultId, 10)} style={{ cursor: 'pointer' }} />}
                                            {!item.inspectionResultId && <p>-</p>}
                                        </div>
                                    </Td>
                                    <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {item.invoiceStatus === INVOICE_STATUS.DRAFT && <p>-</p>}
                                            {item.invoiceStatus === INVOICE_STATUS.APPROVED && <ApprovedIcon />}
                                            {item.invoiceStatus === INVOICE_STATUS.IN_PROGRESS && <NewIcon />}
                                            {item.invoiceStatus === INVOICE_STATUS.REJECTED && <RejectIcon />}
                                        </div>
                                    </Td>
                                    <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {item.paymentStatus === PAYMENT_STATUS.UNPAID && <UnpaidIcon />}
                                            {item.paymentStatus === PAYMENT_STATUS.PAID && <PaidIcon />}
                                            {item.paymentStatus === PAYMENT_STATUS.PENDING && <ApprovedIcon />}
                                            {item.paymentStatus === PAYMENT_STATUS.REJECTED && <RejectIcon />}
                                            {item.paymentStatus === PAYMENT_STATUS.COMPLETED && <CompletedIcon />}
                                        </div>
                                    </Td>
                                    {role === ROLES.FINANCE
                                        ? (<Td textAlign='center'>
                                            <Button isDisabled={item.paymentStatus !== PAYMENT_STATUS.UNPAID || item.invoiceStatus !== INVOICE_STATUS.APPROVED} bgColor='#079455' borderRadius='full' color='white' height='25px' onClick={() => handlePay(item.id)}>Pay</Button>
                                        </Td>)
                                        : (<Td textAlign='center'>
                                            <Button isDisabled={item.invoiceStatus !== INVOICE_STATUS.IN_PROGRESS} bgColor='#0075BE' borderRadius='full' color='white' height='25px' onClick={() => handleView(item.id, item.sequenceID)}>View</Button>
                                        </Td>)
                                    }
                                </Tr>
                            )
                        })}
                    </Tbody>
                </Table>
            </Box>
            <Pagination currentPageSize={currentPageSize} currentPageNumber={currentPageNumber} totalPageNumber={totalPageNumber} handleChangePageSize={handleChangePageSize} handleChangePageNumber={handleChangePageNumber} hasNextPage={hasNextPage} />
        </TableContainer>
    )
}

export default TableInvoiceInspectorSuperAdmin;